import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent, ServiceHandler } from '@nucleus/ui-base';
import { UserDataTypeService } from '../../services/userdata-type/userdata-type.service';
import { UserDataTypeReadOnlyStore } from '../../services/userdata-type/userdata-type-readonly.store';
import {
    ReExtractAttributesResult,
    UserDataRuleSource,
    UserDataTypeAttribute,
    UserDataTypeAttributeUpdateRequest,
    UserDataTypeDetail,
    UserDataTypeOptions,
    UserDataTypeSummary,
    UserDataTypeTemplate,
    UserDataTypeTemplateUpdateRequest,
    UserDataValueType,
} from '../../services/userdata-type/userdata-type.models';
import {
    UserDataTypeAttributeDialogComponent,
    UserDataTypeAttributeDialogData,
} from './userdata-type-attribute-dialog.component';
import {
    UserDataTypeTemplateDialogComponent,
    UserDataTypeTemplateDialogData,
} from './userdata-type-template-dialog.component';

// TODO: page-help-context entry for /administration/userdata-types/:type

/**
 * Detail page for a single UserData type.
 *
 * Three tabs:
 *  - Attributes — user_data_type_attribute rules; inline edit of valueType,
 *    indexed; lock/unlock to flip INFERRED ↔ MANUAL; row-level edit + delete;
 *    bulk re-extract action above the table.
 *  - Templates — user_data_type_template rules with sourceType=this type
 *    (incoming hydration), plus a read-only block showing rules that
 *    reference this type as their templateType (outgoing).
 *  - Sample data — last 10 user_data rows of this type, raw payload viewed
 *    via ngx-json-viewer for rule-authoring guidance.
 */
@Component({
    selector: 'app-userdata-type-detail',
    templateUrl: './userdata-type-detail.component.html',
    styleUrl: './userdata-type-detail.component.css',
    standalone: false,
})
export class UserDataTypeDetailComponent extends BaseComponent implements OnInit {

    private userDataTypeService: UserDataTypeService = inject(UserDataTypeService);
    private readOnlyStore: UserDataTypeReadOnlyStore = inject(UserDataTypeReadOnlyStore);
    private route: ActivatedRoute = inject(ActivatedRoute);
    protected override router: Router = inject(Router);
    protected override dialog: MatDialog = inject(MatDialog);

    type            = signal<string>('');
    detail          = signal<UserDataTypeDetail | null>(null);
    attributes      = signal<UserDataTypeAttribute[]>([]);
    templates       = signal<UserDataTypeTemplate[]>([]);
    incomingTemplates = signal<UserDataTypeTemplate[]>([]);
    /** Per-type options as last fetched/persisted — the "saved" baseline
     *  the draft is compared against to compute dirtiness. */
    options         = signal<UserDataTypeOptions | null>(null);
    /** Local draft — what the operator is currently editing. Each toolbar
     *  control mutates the draft; nothing is persisted until
     *  {@link onSaveOptions} fires. {@link onResetOptions} drops the draft
     *  back to the last-saved {@link options}. */
    draftOptions    = signal<UserDataTypeOptions | null>(null);
    /** True when the draft has diverged from the last-saved options.
     *  Drives the Save/Reset button enabled state. */
    isOptionsDirty  = computed<boolean>(() => {
        const s = this.options();
        const d = this.draftOptions();
        if (!s && !d) return false;
        if (!s || !d) return true;
        return JSON.stringify(s) !== JSON.stringify(d);
    });
    /** Saving flag for the toolbar Save button. */
    savingOptions   = signal<boolean>(false);

    /** Sample-card expansion state on the "Sample data" tab — keyed by the
     *  row's UserData key. Default empty = all sample cards collapsed; the
     *  operator expands the ones they want to inspect. */
    expandedSamples = signal<Set<string>>(new Set());

    isSampleExpanded(key: string): boolean {
        return this.expandedSamples().has(key);
    }

    toggleSample(key: string): void {
        this.expandedSamples.update(s => {
            const next = new Set(s);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    /** Static ISO 4217 currency catalog feeding the Currency dropdown. */
    readonly CURRENCY_CODES: string[] = [
        'USD', 'CAD', 'EUR', 'GBP', 'AUD', 'JPY', 'CHF', 'CNY',
        'INR', 'MXN', 'BRL', 'ZAR', 'NZD', 'SGD', 'HKD', 'KRW',
        'SEK', 'NOK', 'DKK', 'PLN', 'TRY', 'AED',
    ];
    /** Active tab index (0=Attributes, 1=Templates, 2=Sample data). Drives
     *  the help-drawer content swap. */
    activeTabIndex  = signal<number>(0);
    /** Source the operator arrived from (?from=… query param). Drives the
     *  Back-button destination so we return to where they came from
     *  instead of always landing on the userdata-types list. */
    backOrigin      = signal<'types' | 'user-data'>('types');
    backTo          = computed<string>(() => this.backOrigin() === 'user-data'
        ? '/scheduling/user-data'
        : '/administration/userdata-types');
    backLabel       = computed<string>(() => this.backOrigin() === 'user-data'
        ? 'Back to user data'
        : 'Back to types');
    /** Other types observed in the deployment — used to autocomplete the
     *  templateType field in the create/edit template dialog. */
    knownTypes      = signal<string[]>([]);
    loading         = signal<boolean>(true);
    /** Per-row "saving" set for inline edits, so toggling one row doesn't
     *  disable the rest while the request is in flight. */
    savingRows      = signal<Set<string>>(new Set());
    /** Per-template-row save set, keyed by targetPath. */
    savingTemplateRows = signal<Set<string>>(new Set());

    readonly attributeColumns = ['name', 'payloadPath', 'valueType', 'indexed',
                                 'readOnly', 'source', 'sampleCount', 'star'];
    readonly templateColumns = ['referencePath', 'mapping', 'targetPath', 'overwrite',
                                'source', 'confidence', 'sampleCount', 'star'];
    readonly incomingTemplateColumns = ['referencePath', 'targetType', 'targetPath',
                                        'overwrite', 'source', 'confidence'];

    readonly VALUE_TYPES: UserDataValueType[] = ['string', 'integer', 'semver'];

    /** True when the type is observed but has zero rows yet. */
    isEmpty = computed<boolean>(() => (this.detail()?.rowCount ?? 0) === 0);

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToUserDataTypeManagement()) {
            this.showAccessDeny();
            return;
        }
        this.route.paramMap.subscribe(params => {
            const type = params.get('type') ?? '';
            this.type.set(type);
            if (type) {
                this.reload();
            }
        });
        // ?from=user-data means the operator clicked the Type column on
        // /scheduling/user-data; route the Back button there instead of
        // the default userdata-types list.
        this.route.queryParamMap.subscribe(qp => {
            const from = qp.get('from');
            this.backOrigin.set(from === 'user-data' ? 'user-data' : 'types');
        });
    }

    /** Fetch everything for the current type. Independent endpoints; we kick
     *  them off in parallel and let the spinners settle as each lands. */
    reload(): void {
        const type = this.type();
        if (!type) return;
        this.loading.set(true);
        this.loadDetail(type);
        this.loadAttributes(type);
        this.loadTemplates(type);
        this.loadIncomingTemplates(type);
        this.loadKnownTypes();
        this.loadOptions(type);
    }

    private loadOptions(type: string): void {
        this.userDataTypeService.getOptions({
            onStart: () => {},
            onSuccess: (o) => {
                this.options.set(o);
                this.draftOptions.set(this.cloneOptions(o));
            },
            onError: () => {
                const seed: UserDataTypeOptions = { type, strict: false };
                this.options.set(seed);
                this.draftOptions.set({ ...seed });
            },
            onComplete: () => {},
        }, type);
    }

    // ── UserData v2 — type-options draft editing (P2.2) ────────────────────
    //
    // Each control wires through patchDraft() to mutate the in-memory draft.
    // The Save button (onSaveOptions) is the only path that hits the backend.
    // onResetOptions reverts the draft to the last-saved options.

    /** Patch the draft with a partial update. No-op when the draft is null
     *  (still loading). */
    /** NUC-RBAC v2: mutate gate for the whole type-detail page. Mutating the
     *  type config (options / attributes / templates) needs userdata-type:update. */
    canManage(): boolean {
        return this.permissionChecker.hasAccessToManageUserDataTypes();
    }

    /** Click-handler guard — early-returns with an access-deny toast when the
     *  user lacks userdata-type:update, so a DOM/programmatic click on a hidden
     *  control can't reach a mutating call. */
    private requireManage(): boolean {
        if (!this.canManage()) { this.showAccessDeny(); return false; }
        return true;
    }

    patchDraft(patch: Partial<UserDataTypeOptions>): void {
        const current = this.draftOptions();
        if (!current) return;
        this.draftOptions.set({ ...current, ...patch });
    }

    onToggleStrict(strict: boolean)             { this.patchDraft({ strict }); }
    onToggleResourceDefault(checked: boolean)   { this.patchDraft({ resourceDefault: checked }); }
    onToggleReusableDefault(checked: boolean)   { this.patchDraft({ reusableDefault: checked }); }
    onCapacityUnitChange(value: string)         {
        this.patchDraft({ resourceCapacityUnit: value?.trim() || null });
    }
    onCostDefaultChange(field: 'costPerUseDefault' | 'costWarningThresholdMonthly',
                        value: number | null) {
        this.patchDraft({ [field]: value });
    }
    onCostCurrencyChange(value: string | null) {
        this.patchDraft({ costCurrencyDefault: value?.trim().toUpperCase() || null });
    }
    onToggleManualCreate(checked: boolean)      { this.patchDraft({ manualCreate: checked }); }
    onToggleManualEdit(checked: boolean)        { this.patchDraft({ manualEdit: checked }); }

    /** Persist the current draft. Disabled by the template when the draft
     *  matches the last-saved options. */
    onSaveOptions(): void {
        if (!this.requireManage()) return;
        const type = this.type();
        const draft = this.draftOptions();
        if (!type || !draft || !this.isOptionsDirty()) return;
        this.savingOptions.set(true);
        this.userDataTypeService.setOptionsV2({
            onStart: () => {},
            onSuccess: (o) => {
                this.options.set(o);
                this.draftOptions.set(this.cloneOptions(o));
                this.showSuccess('Type options saved.');
            },
            onError: (e) => this.showError(e),
            onComplete: () => this.savingOptions.set(false),
        }, type, draft);
    }

    /** Revert the draft to the last-saved options. */
    onResetOptions(): void {
        const saved = this.options();
        this.draftOptions.set(this.cloneOptions(saved));
    }

    private cloneOptions(o: UserDataTypeOptions | null): UserDataTypeOptions | null {
        return o ? { ...o } : null;
    }

    // ── Help drawer (P2.2 — collapsible, persisted per-user via localStorage) ──

    /** Storage key for the help-drawer collapsed state. Per-page so the
     *  user-data-types help collapse doesn't affect other admin pages. */
    private static readonly HELP_COLLAPSED_KEY = 'ts.udtypes.help.collapsed';

    /** True = drawer collapsed (only the toggle button visible). Default
     *  open on first visit; flips to remembered state on subsequent loads. */
    readonly helpCollapsed = signal<boolean>(this.readHelpCollapsed());

    toggleHelp(): void {
        const next = !this.helpCollapsed();
        this.helpCollapsed.set(next);
        try {
            localStorage.setItem(UserDataTypeDetailComponent.HELP_COLLAPSED_KEY, next ? '1' : '0');
        } catch {
            // localStorage unavailable (e.g. private browsing) — fall through; the
            // toggle still works for this session, just doesn't survive reload.
        }
    }

    private readHelpCollapsed(): boolean {
        try {
            return localStorage.getItem(UserDataTypeDetailComponent.HELP_COLLAPSED_KEY) === '1';
        } catch {
            return false;
        }
    }

    backToList(): void {
        this.router.navigate([this.backTo()]);
    }

    // -- Attribute actions ---------------------------------------------------

    openCreateAttribute(): void {
        if (!this.requireManage()) return;
        const data: UserDataTypeAttributeDialogData = {
            type: this.type(),
            attribute: null,
        };
        const ref = this.dialog.open(UserDataTypeAttributeDialogComponent, {
            data,
            panelClass: 'bw-dialog-panel',
            autoFocus: 'first-tabbable',
            width: '640px',
            maxWidth: '95vw',
        });
        ref.afterClosed().subscribe(saved => {
            if (saved) {
                this.showSuccess(`Attribute "${saved.name}" created`);
                this.loadAttributes(this.type());
                this.loadDetail(this.type());
            }
        });
    }

    openEditAttribute(attr: UserDataTypeAttribute): void {
        if (!this.requireManage()) return;
        const data: UserDataTypeAttributeDialogData = {
            type: this.type(),
            attribute: attr,
        };
        const ref = this.dialog.open(UserDataTypeAttributeDialogComponent, {
            data,
            panelClass: 'bw-dialog-panel',
            autoFocus: 'first-tabbable',
            width: '640px',
            maxWidth: '95vw',
        });
        ref.afterClosed().subscribe(saved => {
            if (saved) {
                this.showSuccess(`Attribute "${saved.name}" updated`);
                this.loadAttributes(this.type());
            }
        });
    }

    setAttributeValueType(attr: UserDataTypeAttribute, valueType: UserDataValueType): void {
        if (attr.valueType === valueType) return;
        this.updateAttribute(attr, { valueType });
    }

    setAttributeIndexed(attr: UserDataTypeAttribute, indexed: boolean): void {
        if (attr.indexed === indexed) return;
        this.updateAttribute(attr, { indexed });
    }

    /** Flip INFERRED ↔ MANUAL. Locking preserves the row unchanged; unlocking
     *  hands the row back to the inference job, which is free to adjust on
     *  the next pass. */
    toggleAttributeLock(attr: UserDataTypeAttribute): void {
        const next: UserDataRuleSource = attr.source === 'MANUAL' ? 'INFERRED' : 'MANUAL';
        this.updateAttribute(attr, { source: next });
    }

    async deleteAttribute(attr: UserDataTypeAttribute): Promise<void> {
        if (!this.requireManage()) return;
        const ok = await this.confirm(
            'Delete attribute',
            `Delete attribute "${attr.name}"? Any indexed values will be removed.`,
        );
        if (!ok) return;
        this.markAttributeSaving(attr.name, true);
        const handler: ServiceHandler<void> = {
            onStart: () => {},
            onSuccess: () => {
                this.showSuccess(`Attribute "${attr.name}" deleted`);
                this.loadAttributes(this.type());
                this.loadDetail(this.type());
            },
            onError: (err) => {
                this.showError(err, 'Delete failed');
            },
            onComplete: () => {
                this.markAttributeSaving(attr.name, false);
            },
        };
        this.userDataTypeService.deleteAttribute(handler, this.type(), attr.name);
    }

    async reExtractAttributes(): Promise<void> {
        if (!this.requireManage()) return;
        const detail = this.detail();
        const rowCount = detail?.rowCount ?? 0;
        // Strict mode + zero declared attributes is a footgun — re-extract
        // can only refresh sample counts for EXISTING rules, and there are
        // none. Warn the operator explicitly instead of silently emitting 0
        // attribute samples (the bug surfaced when a user set Strict on a
        // brand-new type, clicked Re-extract, and saw nothing happen).
        const strict      = !!this.options()?.strict;
        const attrCount   = this.attributes().length;
        const strictTrap  = strict && attrCount === 0;
        const body        = strictTrap
            ? `<p>This will re-process ${rowCount} ${rowCount === 1 ? 'row' : 'rows'} of `
              + `<b>${this.type()}</b>.</p>`
              + `<p><b>Strict mode is on and no attributes are declared yet</b> — `
              + `the extractor will not auto-create rules, so no attribute rows will `
              + `appear. Turn Strict off first if you want inference to discover fields.</p>`
              + `<p>Continue anyway?</p>`
            : `This will re-process ${rowCount} ${rowCount === 1 ? 'row' : 'rows'} of `
              + `${this.type()} and refresh inferred attributes. Continue?`;
        const ok = await this.confirm('Re-extract attributes', body);
        if (!ok) return;
        const handler: ServiceHandler<ReExtractAttributesResult> = {
            onStart: () => {},
            onSuccess: (result) => {
                const r = result ?? { rowsProcessed: 0, attributesEmitted: 0 };
                this.showSuccess(
                    `Re-extracted ${r.rowsProcessed} ${r.rowsProcessed === 1 ? 'row' : 'rows'} `
                    + `(${r.attributesEmitted} attribute samples)`,
                );
                this.loadAttributes(this.type());
                this.loadDetail(this.type());
            },
            onError: (err) => {
                this.showError(err, 'Re-extraction failed');
            },
            onComplete: () => {},
        };
        this.userDataTypeService.reExtractAttributes(handler, this.type());
    }

    isAttributeSaving(name: string): boolean {
        return this.savingRows().has(name);
    }

    // -- Template actions ----------------------------------------------------

    openCreateTemplate(): void {
        if (!this.requireManage()) return;
        const data: UserDataTypeTemplateDialogData = {
            sourceType: this.type(),
            template: null,
            knownTypes: this.knownTypes(),
        };
        const ref = this.dialog.open(UserDataTypeTemplateDialogComponent, {
            data,
            panelClass: 'bw-dialog-panel',
            autoFocus: 'first-tabbable',
            width: '720px',
            maxWidth: '95vw',
        });
        ref.afterClosed().subscribe(saved => {
            if (saved) {
                this.showSuccess('Template created');
                this.loadTemplates(this.type());
                this.loadDetail(this.type());
            }
        });
    }

    openEditTemplate(tpl: UserDataTypeTemplate): void {
        if (!this.requireManage()) return;
        const data: UserDataTypeTemplateDialogData = {
            sourceType: this.type(),
            template: tpl,
            knownTypes: this.knownTypes(),
        };
        const ref = this.dialog.open(UserDataTypeTemplateDialogComponent, {
            data,
            panelClass: 'bw-dialog-panel',
            autoFocus: 'first-tabbable',
            width: '720px',
            maxWidth: '95vw',
        });
        ref.afterClosed().subscribe(saved => {
            if (saved) {
                this.showSuccess('Template updated');
                this.loadTemplates(this.type());
            }
        });
    }

    setTemplateOverwrite(tpl: UserDataTypeTemplate, overwrite: boolean): void {
        if (tpl.overwrite === overwrite) return;
        this.updateTemplate(tpl, { overwrite });
    }

    toggleTemplateLock(tpl: UserDataTypeTemplate): void {
        const next: UserDataRuleSource = tpl.source === 'MANUAL' ? 'INFERRED' : 'MANUAL';
        this.updateTemplate(tpl, { source: next });
    }

    async deleteTemplate(tpl: UserDataTypeTemplate): Promise<void> {
        if (!this.requireManage()) return;
        const ok = await this.confirm(
            'Delete template',
            `Delete template rule for target "${tpl.targetPath}"?`,
        );
        if (!ok) return;
        this.markTemplateSaving(tpl.targetPath, true);
        const handler: ServiceHandler<void> = {
            onStart: () => {},
            onSuccess: () => {
                this.showSuccess('Template deleted');
                this.loadTemplates(this.type());
                this.loadDetail(this.type());
            },
            onError: (err) => {
                this.showError(err, 'Delete failed');
            },
            onComplete: () => {
                this.markTemplateSaving(tpl.targetPath, false);
            },
        };
        this.userDataTypeService.deleteTemplate(handler, this.type(), tpl.targetPath);
    }

    isTemplateSaving(targetPath: string): boolean {
        return this.savingTemplateRows().has(targetPath);
    }

    // -- Display helpers -----------------------------------------------------

    /** Visual mapping label for the templates table:
     *  "EQUIPMENT_TYPE.$.vendor" — matches the pattern from the spec. */
    formatTemplateMapping(tpl: UserDataTypeTemplate): string {
        return `${tpl.templateType}.${tpl.fieldPath}`;
    }

    sourceChipClass(source: UserDataRuleSource | undefined): string {
        return source === 'MANUAL' ? 'source-chip source-manual'
                                   : 'source-chip source-inferred';
    }

    confidencePercent(value: number | undefined): string {
        if (value === undefined || value === null || isNaN(value)) return '—';
        // Backend confidence is 0..1; render as %.
        const pct = Math.round((value > 1 ? value : value * 100));
        return `${pct}%`;
    }

    // -- Internal load helpers ----------------------------------------------

    private loadDetail(type: string): void {
        const handler: ServiceHandler<UserDataTypeDetail> = {
            onStart: () => {},
            onSuccess: (data) => {
                this.detail.set(data ?? null);
            },
            onError: (err) => {
                if (this.is404(err)) {
                    // Build a placeholder so the rest of the UI renders even
                    // when the backend can't yet aggregate this type. Empty
                    // recentRows triggers the "no sample data" tab content.
                    this.detail.set({
                        type,
                        rowCount: 0,
                        attributeCount: 0,
                        templateInCount: 0,
                        templateOutCount: 0,
                        recentRows: [],
                    });
                    return;
                }
                this.showError(err, 'Failed to load type detail');
            },
            onComplete: () => {
                this.loading.set(false);
            },
        };
        this.userDataTypeService.getDetail(handler, type);
    }

    private loadAttributes(type: string): void {
        const handler: ServiceHandler<UserDataTypeAttribute[]> = {
            onStart: () => {},
            onSuccess: (data) => {
                // Overlay readOnly from the client-side store — the backend
                // doesn't yet persist user_data_type_attribute.read_only.
                const merged = (data ?? []).map(r => ({
                    ...r,
                    readOnly: this.readOnlyStore.isReadOnly(type, r.name) || (r as any).readOnly === true,
                }));
                this.attributes.set(merged);
            },
            onError: (err) => {
                this.attributes.set([]);
                if (!this.is404(err)) this.showError(err, 'Failed to load attributes');
            },
            onComplete: () => {},
        };
        this.userDataTypeService.listAttributes(handler, type);
    }

    private loadTemplates(type: string): void {
        const handler: ServiceHandler<UserDataTypeTemplate[]> = {
            onStart: () => {},
            onSuccess: (data) => {
                this.templates.set(data ?? []);
            },
            onError: (err) => {
                this.templates.set([]);
                if (!this.is404(err)) this.showError(err, 'Failed to load templates');
            },
            onComplete: () => {},
        };
        this.userDataTypeService.listTemplates(handler, type);
    }

    private loadIncomingTemplates(type: string): void {
        const handler: ServiceHandler<UserDataTypeTemplate[]> = {
            onStart: () => {},
            onSuccess: (data) => {
                this.incomingTemplates.set(data ?? []);
            },
            onError: (err) => {
                this.incomingTemplates.set([]);
                if (!this.is404(err)) this.showError(err, 'Failed to load incoming templates');
            },
            onComplete: () => {},
        };
        this.userDataTypeService.listIncomingTemplates(handler, type);
    }

    private loadKnownTypes(): void {
        const handler: ServiceHandler<UserDataTypeSummary[]> = {
            onStart: () => {},
            onSuccess: (data) => {
                this.knownTypes.set((data ?? []).map(t => t.type).sort());
            },
            onError: () => {
                this.knownTypes.set([]);
            },
            onComplete: () => {},
        };
        this.userDataTypeService.listObserved(handler);
    }

    private updateAttribute(attr: UserDataTypeAttribute, body: UserDataTypeAttributeUpdateRequest): void {
        if (!this.requireManage()) { this.loadAttributes(this.type()); return; }
        this.markAttributeSaving(attr.name, true);
        const handler: ServiceHandler<UserDataTypeAttribute> = {
            onStart: () => {},
            onSuccess: () => {
                this.loadAttributes(this.type());
            },
            onError: (err) => {
                this.showError(err, 'Update failed');
                // Refetch so the UI snaps back to the persisted state — we
                // don't optimistically mutate, so the toggle visually flips
                // back when the refetch lands.
                this.loadAttributes(this.type());
            },
            onComplete: () => {
                this.markAttributeSaving(attr.name, false);
            },
        };
        this.userDataTypeService.updateAttribute(handler, this.type(), attr.name, body);
    }

    private updateTemplate(tpl: UserDataTypeTemplate, body: UserDataTypeTemplateUpdateRequest): void {
        if (!this.requireManage()) { this.loadTemplates(this.type()); return; }
        this.markTemplateSaving(tpl.targetPath, true);
        const handler: ServiceHandler<UserDataTypeTemplate> = {
            onStart: () => {},
            onSuccess: () => {
                this.loadTemplates(this.type());
            },
            onError: (err) => {
                this.showError(err, 'Update failed');
                this.loadTemplates(this.type());
            },
            onComplete: () => {
                this.markTemplateSaving(tpl.targetPath, false);
            },
        };
        this.userDataTypeService.updateTemplate(handler, this.type(), tpl.targetPath, body);
    }

    private markAttributeSaving(name: string, saving: boolean): void {
        this.savingRows.update(set => {
            const next = new Set(set);
            if (saving) next.add(name);
            else next.delete(name);
            return next;
        });
    }

    private markTemplateSaving(targetPath: string, saving: boolean): void {
        this.savingTemplateRows.update(set => {
            const next = new Set(set);
            if (saving) next.add(targetPath);
            else next.delete(targetPath);
            return next;
        });
    }

    /** Heuristic — matches the same string the RequestHandler emits for 404s. */
    private is404(err: unknown): boolean {
        return typeof err === 'string' && /404|couldn't find/i.test(err);
    }
}
